import { getProcoreApiBaseUrl } from "./auth";
import { procoreClient } from "./client";
import { parseFlexibleResponse } from "./utils";

type ProcoreSignedDownloadUrlResponse =
    | string
    | { signed_download_url: string }
    | { data: { signed_download_url: string } };

type SignedUrlCacheEntry = {
    /** In-flight or settled promise for the signed URL. Settled promises are reused until the underlying URL is near expiry. */
    promise: Promise<string>;
    /** Unix-epoch seconds at which the cached URL stops being usable. `undefined` while the request is in-flight. */
    expiresAt?: number;
};

/**
 * Refresh the cached URL this many seconds before its `expiresAt` so that
 * a slow request can still complete before the URL becomes unusable.
 */
const SIGNED_URL_REFRESH_MARGIN_SECONDS = 60;

const signedUrlCache = new Map<string, SignedUrlCacheEntry>();

function getCacheKey(params: {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
}): string {
    return `${params.companyId}:${params.projectId}:${params.sceneId}:${params.sceneVersionId}`;
}

/**
 * Procore signed URLs include `expiresAt=<unix-seconds>` in their query string.
 * Returns `undefined` when the URL doesn't follow that shape so the caller
 * decides whether to keep or evict the entry.
 */
function parseExpiresAt(signedUrl: string): number | undefined {
    try {
        const url = new URL(signedUrl, "https://placeholder.invalid");
        const value = url.searchParams.get("expiresAt");
        if (!value) {
            return undefined;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function isCacheEntryFresh(entry: SignedUrlCacheEntry, nowSeconds: number): boolean {
    // In-flight requests have no `expiresAt` yet — always reuse them so concurrent callers dedupe.
    if (entry.expiresAt === undefined) {
        return true;
    }
    return entry.expiresAt - SIGNED_URL_REFRESH_MARGIN_SECONDS > nowSeconds;
}

async function fetchSignedDownloadUrl(params: {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
}): Promise<string> {
    // Use custom parseResponse to handle both JSON and plain text responses
    const raw = await procoreClient<string>(
        `/rest/v2.0/companies/${params.companyId}/projects/${params.projectId}/bim/scenes/${params.sceneId}/versions/${params.sceneVersionId}/signed_download_urls`,
        {
            method: "POST",
            parseResponse: async (res) => res.text(),
        },
    );

    const parsed = parseFlexibleResponse<ProcoreSignedDownloadUrlResponse>(raw);

    if (typeof parsed === "string") {
        // Handle raw string or JSON string (already parsed by JSON.parse).
        return parsed.trim().replace(/^"|"$/g, "");
    }

    if (parsed && typeof parsed === "object") {
        if ("signed_download_url" in parsed) {
            return parsed.signed_download_url;
        }

        if ("data" in parsed && parsed.data?.signed_download_url) {
            return parsed.data.signed_download_url;
        }
    }

    throw new Error("signed_download_url missing from response");
}

export async function createBimSignedDownloadUrl(params: {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
}): Promise<string> {
    const cacheKey = getCacheKey(params);
    const nowSeconds = Math.floor(Date.now() / 1000);

    const cached = signedUrlCache.get(cacheKey);
    if (cached && isCacheEntryFresh(cached, nowSeconds)) {
        return cached.promise;
    }

    const promise = fetchSignedDownloadUrl(params);
    const entry: SignedUrlCacheEntry = { promise };
    signedUrlCache.set(cacheKey, entry);

    promise.then(
        (signedUrl) => {
            // Only mutate the cache if it still points at this in-flight entry.
            // A concurrent failure (or manual eviction) may have replaced it.
            if (signedUrlCache.get(cacheKey) !== entry) {
                return;
            }
            const expiresAt = parseExpiresAt(signedUrl);
            if (expiresAt === undefined) {
                signedUrlCache.delete(cacheKey);
                return;
            }
            entry.expiresAt = expiresAt;
        },
        () => {
            if (signedUrlCache.get(cacheKey) === entry) {
                signedUrlCache.delete(cacheKey);
            }
        },
    );

    return promise;
}

/** Test/debug helper: drop all cached signed URLs. */
export function clearBimSignedDownloadUrlCache(): void {
    signedUrlCache.clear();
}

export type BimSignedFileUrlParams = {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
};

/**
 * Build a fully-formed FAS URL for a single file path within a scene version.
 *
 * Combines the cached, proxy-rewritten signed download URL for the scene with
 * the per-file `?file=<path>` parameter that Procore FAS uses to address
 * individual artifacts. Returns a string suitable for `new URL(...)` or a
 * direct `fetch()`.
 *
 * Designed to be safe to call repeatedly from a hot path (e.g. the SDK's
 * async `urlBuilder`): the underlying signed URL is cached + deduped per
 * scene version and refreshed transparently as it nears expiry.
 */
export async function createBimSignedFileUrl(params: BimSignedFileUrlParams, filePath: string): Promise<string> {
    const signedUrl = await createBimSignedDownloadUrl(params);
    const rewrittenUrl = rewriteSignedUrlForProxy(signedUrl);
    const isRelativeUrl = rewrittenUrl.startsWith("/");
    const url = new URL(rewrittenUrl, "https://placeholder.invalid");
    url.searchParams.set("file", filePath);

    return isRelativeUrl ? `${url.pathname}${url.search}${url.hash}` : url.toString();
}

/**
 * Build a synthesized FAS URL for OFFLINE reads — no network, no signature.
 *
 * Used when offline to address scene artifacts without calling
 * `signed_download_urls` (a network POST that fails offline). The Novorender
 * SDK's `requestOfflineFile` / `extractProcoreDirAndFilename` resolves the file
 * from OPFS by matching the `/scenes/{id}` path segment + `?file=` basename and
 * ignores the signature/expiry — so this signature-less URL hits the same OPFS
 * entries written during the (proxied) online download.
 *
 * Contract (must stay in sync with the SDK matcher): the pathname must start
 * with `/fas/api/` and contain `/scenes/{sceneId}`. Pass `filePath` for a
 * per-asset URL (`?file=…`); omit it for the scene-base URL used as the
 * `loadScene` placeholder.
 */
export function buildOfflineFasUrl(params: BimSignedFileUrlParams, filePath?: string): URL {
    const url = new URL(
        `/fas/api/v5.1/files/companies/${params.companyId}/projects/${params.projectId}/scenes/${params.sceneId}/versions/${params.sceneVersionId}`,
        window.location.origin,
    );
    if (filePath) {
        url.searchParams.set("file", filePath);
    }
    return url;
}

/**
 * Rewrite signed download URL to use local proxy if needed
 *
 * When using Vite proxy for local development, we need to rewrite
 * absolute Procore URLs to use the proxy path to avoid CORS issues.
 *
 * @param signedUrl - The signed URL from Procore API
 * @returns Rewritten URL suitable for local development or original URL
 */
export function rewriteSignedUrlForProxy(signedUrl: string): string {
    const baseUrl = getProcoreApiBaseUrl();

    // If baseUrl is a relative path (like /procore-api), we're using a proxy
    // and need to rewrite the signed URL to use the proxy as well
    if (baseUrl.startsWith("/")) {
        try {
            const url = new URL(signedUrl);
            // Replace the origin with our proxy path
            // e.g., https://staging1.procoretech-qa.com/fas/api/... → /procore-api/fas/api/...
            const rewritten = baseUrl + url.pathname + url.search;
            return rewritten;
        } catch (e) {
            console.warn("Failed to rewrite signed URL for proxy:", e);
            return signedUrl;
        }
    }

    // If baseUrl is absolute, return the signed URL as-is (production scenario)
    return signedUrl;
}
